import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, isSupported } from 'firebase/messaging';
import { request } from '../api/client';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

export function isFcmConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId && VAPID_KEY);
}

export async function isPushSupportedHere(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

async function ensureMessaging(): Promise<Messaging> {
  if (messaging) return messaging;
  if (!isFcmConfigured()) throw new Error('Firebase env not configured');
  if (!app) app = initializeApp(firebaseConfig);
  messaging = getMessaging(app);
  return messaging;
}

function swUrl(): string {
  const q = new URLSearchParams({
    apiKey: firebaseConfig.apiKey ?? '',
    authDomain: firebaseConfig.authDomain ?? '',
    projectId: firebaseConfig.projectId ?? '',
    storageBucket: firebaseConfig.storageBucket ?? '',
    messagingSenderId: firebaseConfig.messagingSenderId ?? '',
    appId: firebaseConfig.appId ?? '',
  });
  return `/firebase-messaging-sw.js?${q.toString()}`;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) throw new Error('Service workers not supported');
  const url = swUrl();
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing && existing.active?.scriptURL.includes('firebase-messaging-sw.js')) {
    return existing;
  }
  return navigator.serviceWorker.register(url, { scope: '/' });
}

export async function enableWebPush(): Promise<{ token: string }> {
  const supported = await isPushSupportedHere();
  if (!supported) throw new Error('This browser does not support push notifications');
  if (!isFcmConfigured()) throw new Error('Firebase is not configured (set VITE_FIREBASE_* env vars)');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications permission was denied');

  const reg = await ensureServiceWorker();
  const m = await ensureMessaging();
  const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
  if (!token) throw new Error('Failed to obtain FCM token');

  await request({
    url: '/notifications/devices/register',
    method: 'POST',
    data: {
      token,
      platform: 'web',
      userAgent: navigator.userAgent,
    },
  });

  return { token };
}

export async function disableWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
  await reg?.unregister();
  try {
    await request({ url: '/notifications/devices/unregister', method: 'POST', data: { platform: 'web' } });
  } catch {
    // server may not care if there was no token — ignore
  }
}

export async function onForegroundMessage(handler: (payload: any) => void): Promise<() => void> {
  const supported = await isPushSupportedHere();
  if (!supported || !isFcmConfigured()) return () => undefined;
  const m = await ensureMessaging();
  return onMessage(m, handler);
}

export function currentPermission(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}
